import { Reveal } from "@/components/common/Reveal";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const TABS = [
  { id: "privacy", label: "Privacy Policy", dotColor: "bg-emerald-500" },
  { id: "terms", label: "Terms of Service", dotColor: "bg-sky-500" },
  { id: "disclosure", label: "Important Disclosure", dotColor: "bg-purple-500" },
  { id: "cookies", label: "Cookie Policy", dotColor: "bg-amber-500" },
] as const;

export type LegalTabId = (typeof TABS)[number]["id"];
export { TABS as LEGAL_TABS };

// Regular background document cards
const BG_CARDS = [
  { title: "GDPR Review", icon: "🛡️" },
  { title: "Data Processing", icon: "✓" },
  { title: "SOC 2 Aligned", icon: "🛡️" },
  { title: "ISO 27001", icon: "🛡️" },
  { title: "Retention Policy", icon: "✓" },
  { title: "Sub-Processor", icon: "🛡️" },
  { title: "Incident Response", icon: "✓" },
  { title: "Data Residency", icon: "✓" },
];

// Tab document cards (clickable, highlighted)
const TAB_CARDS: { tabId: LegalTabId; title: string; dotColor: string }[] = [
  { tabId: "privacy", title: "Privacy Policy", dotColor: "#10b981" },
  { tabId: "terms", title: "Terms of Service", dotColor: "#3b82f6" },
  { tabId: "disclosure", title: "Important Disclosure", dotColor: "#8b5cf6" },
  { tabId: "cookies", title: "Cookie Policy", dotColor: "#f59e0b" },
];

function BgDocCard({ card }: { card: (typeof BG_CARDS)[number] }) {
  return (
    <div className="flex-shrink-0 w-[120px] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 mx-2">
      <div className="space-y-1.5 mb-3">
        <div className="h-[3px] w-[85%] rounded-full bg-[var(--color-fg)] opacity-[0.08]" />
        <div className="h-[3px] w-[70%] rounded-full bg-[var(--color-fg)] opacity-[0.06]" />
        <div className="h-[3px] w-[90%] rounded-full bg-[var(--color-fg)] opacity-[0.08]" />
        <div className="h-[3px] w-[50%] rounded-full bg-[var(--color-fg)] opacity-[0.05]" />
        <div className="h-[3px] w-[80%] rounded-full bg-[var(--color-fg)] opacity-[0.07]" />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[7px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] truncate">
          {card.title}
        </span>
        <span className="text-[8px] text-emerald-500">{card.icon}</span>
      </div>
    </div>
  );
}

function TabDocCard({
  card,
  isActive,
  onClick,
}: {
  card: (typeof TAB_CARDS)[number];
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 w-[130px] border bg-[var(--color-surface)] p-3 mx-2 cursor-pointer transition-all hover:scale-[1.03]"
      style={{
        borderColor: card.dotColor,
        boxShadow: `0 0 12px ${card.dotColor}${isActive ? "30" : "15"}`,
      }}
    >
      <div className="space-y-1.5 mb-3">
        <div className="h-[3px] w-[85%] rounded-full" style={{ backgroundColor: card.dotColor, opacity: 0.25 }} />
        <div className="h-[3px] w-[70%] rounded-full" style={{ backgroundColor: card.dotColor, opacity: 0.15 }} />
        <div className="h-[3px] w-[90%] rounded-full" style={{ backgroundColor: card.dotColor, opacity: 0.25 }} />
        <div className="h-[3px] w-[50%] rounded-full" style={{ backgroundColor: card.dotColor, opacity: 0.12 }} />
        <div className="h-[3px] w-[80%] rounded-full" style={{ backgroundColor: card.dotColor, opacity: 0.2 }} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[7px] font-[family-name:var(--font-mono)] truncate" style={{ color: card.dotColor }}>
          {card.title}
        </span>
        <span className="text-[8px]" style={{ color: card.dotColor }}>
          {isActive ? "●" : "●"}
        </span>
      </div>
    </button>
  );
}

interface LegalHeroProps {
  activeTab: LegalTabId;
  onTabChange: (tab: LegalTabId) => void;
}

export function LegalHero({ activeTab, onTabChange }: LegalHeroProps) {
  const prefersReducedMotion = useReducedMotion();

  // Build the strip: bg cards interspersed with tab cards, repeated for seamless loop
  const oneSet = [
    ...BG_CARDS.slice(0, 2).map((c) => ({ type: "bg" as const, card: c })),
    { type: "tab" as const, card: TAB_CARDS[0]! },
    ...BG_CARDS.slice(2, 4).map((c) => ({ type: "bg" as const, card: c })),
    { type: "tab" as const, card: TAB_CARDS[1]! },
    ...BG_CARDS.slice(4, 6).map((c) => ({ type: "bg" as const, card: c })),
    { type: "tab" as const, card: TAB_CARDS[2]! },
    ...BG_CARDS.slice(6, 8).map((c) => ({ type: "bg" as const, card: c })),
    { type: "tab" as const, card: TAB_CARDS[3]! },
  ];
  const strip = [...oneSet, ...oneSet, ...oneSet, ...oneSet];

  return (
    <section className="relative z-10 flex flex-col items-center pt-[var(--topnav-height)] pb-0 overflow-hidden">
      <div className="max-w-6xl mx-auto w-full text-center pt-16 mb-10 px-8">
        <Reveal>
          <h1 className="text-3xl md:text-5xl font-light tracking-tight mb-4 font-[family-name:var(--font-display)]">
            Legal
          </h1>
        </Reveal>
        <Reveal delay={100}>
          <p className="text-lg text-[var(--color-fg-secondary)] max-w-2xl mx-auto font-light mb-10">
            Privacy, terms, and policies that govern your use of COSX.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <nav className="flex flex-wrap justify-center gap-4">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm border-b transition-colors cursor-pointer ${
                  activeTab === tab.id
                    ? "text-[var(--color-fg)] border-[var(--color-fg)]"
                    : "text-[var(--color-fg-secondary)] border-[var(--color-border)] hover:text-[var(--color-fg)] hover:border-[var(--color-fg-secondary)]"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${tab.dotColor}`} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </Reveal>
      </div>

      {/* Document flow marquee strip */}
      <Reveal delay={300}>
        <div className="w-screen relative overflow-hidden mt-8 md:mt-12 hero-strip-mask">
          <div
            className={`flex items-start w-max py-6 ${
              prefersReducedMotion ? "" : "animate-[marqueeLeft_80s_linear_infinite] hover:[animation-play-state:paused]"
            }`}
          >
            {strip.map((item, i) =>
              item.type === "tab" ? (
                <TabDocCard
                  key={`tab-${item.card.tabId}-${i}`}
                  card={item.card}
                  isActive={activeTab === item.card.tabId}
                  onClick={() => onTabChange(item.card.tabId)}
                />
              ) : (
                <BgDocCard key={`bg-${item.card.title}-${i}`} card={item.card} />
              ),
            )}
          </div>
        </div>
      </Reveal>
    </section>
  );
}
